import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getTheme, THEMES } from '../themes/index.js';
import type { LayoutFeatures, Theme, ThemeLook } from '../themes/types.js';
import { installThemeStyles } from './css.js';
import {
  DEVICE_SCOPE,
  readPreference,
  writePreference,
  type Appearance,
  type PreferenceStorage,
  type ThemePreference,
} from './storage.js';

export interface ThemeContextValue {
  readonly themes: readonly Theme[];
  readonly theme: Theme;
  /** The look in use: the theme's own, or its alternate for the other colour scheme. */
  readonly look: ThemeLook;
  readonly colorScheme: 'light' | 'dark';
  readonly appearance: Appearance;
  /** The theme's default features with the user's own switches applied. */
  readonly features: LayoutFeatures;
  /** Applies the theme's look and its default features (clears feature switches). */
  setTheme(id: string): void;
  setAppearance(appearance: Appearance): void;
  setFeature<K extends keyof LayoutFeatures>(key: K, value: LayoutFeatures[K]): void;
  resetFeatures(): void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  /** Storage namespace per app: `staff`, `customer`. */
  app: string;
  /** Whose preference: a user id once signed in; the device before that. */
  scope?: string | null | undefined;
  /** Used when nothing is stored (outlet default, or the KDS default). */
  defaultThemeId?: string | undefined;
  /** Test seam; defaults to localStorage (guarded). */
  storage?: PreferenceStorage | null | undefined;
  /** The element that carries data-theme; defaults to <html>. */
  root?: HTMLElement | null | undefined;
  children: ReactNode;
}

function systemPrefersDark(): boolean {
  try {
    return (
      typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
    );
  } catch {
    return false;
  }
}

function initialPreference(
  app: string,
  scope: string,
  defaultThemeId: string | undefined,
  storage: PreferenceStorage | null | undefined,
): ThemePreference {
  const read = (s: string) =>
    storage === undefined ? readPreference(app, s) : readPreference(app, s, storage);
  // A user without a stored choice starts from what this device shows.
  const stored = read(scope) ?? (scope === DEVICE_SCOPE ? null : read(DEVICE_SCOPE));
  return stored ?? { themeId: getTheme(defaultThemeId).id, appearance: 'system', features: {} };
}

/** Picks the look for the wanted colour scheme when the theme has both. */
export function resolveLook(theme: Theme, appearance: Appearance, prefersDark: boolean): ThemeLook {
  if (!theme.alternate) return theme.look;
  const wanted = appearance === 'system' ? (prefersDark ? 'dark' : 'light') : appearance;
  return theme.alternate.colorScheme === wanted ? theme.alternate : theme.look;
}

export function ThemeProvider({
  app,
  scope,
  defaultThemeId,
  storage,
  root,
  children,
}: ThemeProviderProps) {
  const effectiveScope = scope ?? DEVICE_SCOPE;
  const [state, setState] = useState(() => ({
    scope: effectiveScope,
    defaultId: defaultThemeId,
    pref: initialPreference(app, effectiveScope, defaultThemeId, storage),
  }));
  // A different user signed in (or out): load their preference. The default theme
  // changing (the outlet's arrives after sign-in) re-reads it too: a stored choice of
  // the user wins, otherwise the new default applies.
  let pref = state.pref;
  if (state.scope !== effectiveScope || state.defaultId !== defaultThemeId) {
    pref = initialPreference(app, effectiveScope, defaultThemeId, storage);
    setState({ scope: effectiveScope, defaultId: defaultThemeId, pref });
  }
  const [prefersDark, setPrefersDark] = useState(systemPrefersDark);

  useEffect(() => {
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      return;
    }
    const onChange = () => setPrefersDark(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const theme = getTheme(pref.themeId);
  const look = resolveLook(theme, pref.appearance, prefersDark);
  const features = useMemo(() => {
    const merged: LayoutFeatures = { ...theme.features };
    for (const [key, value] of Object.entries(pref.features)) {
      if (value !== undefined) Object.assign(merged, { [key]: value });
    }
    return merged;
  }, [theme, pref.features]);

  useLayoutEffect(() => {
    const el = root ?? document.documentElement;
    installThemeStyles(THEMES, el.ownerDocument);
    el.dataset['theme'] = theme.id;
    el.dataset['scheme'] = look.colorScheme;
    el.dataset['density'] = features.density;
    el.dataset['categoryColours'] = String(features.categoryColours);
  }, [root, theme.id, look.colorScheme, features.density, features.categoryColours]);

  const update = useCallback(
    (change: (p: ThemePreference) => ThemePreference) => {
      setState((s) => {
        const next = change(s.pref);
        if (storage === undefined) writePreference(app, s.scope, next);
        else writePreference(app, s.scope, next, storage);
        return { ...s, pref: next };
      });
    },
    [app, storage],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({
      themes: THEMES,
      theme,
      look,
      colorScheme: look.colorScheme,
      appearance: pref.appearance,
      features,
      setTheme: (id) => update((p) => ({ ...p, themeId: getTheme(id).id, features: {} })),
      setAppearance: (appearance) => update((p) => ({ ...p, appearance })),
      setFeature: (key, v) => update((p) => ({ ...p, features: { ...p.features, [key]: v } })),
      resetFeatures: () => update((p) => ({ ...p, features: {} })),
    }),
    [theme, look, pref.appearance, features, update],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
